"use client"

import { useState, useEffect } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts"
import {
  Avatar,
  Box,
  Card,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material"
import {
  FolderOpen,
  Timeline,
  CheckCircle,
  Warning,
  Refresh,
  Info,
  Close,
  Speed,
  ArrowForward,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

const Dashboard = () => {
  const [projects, setProjects] = useState([])
  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [employeeId, setEmployeeId] = useState(null);

  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    inProgressProjects: 0,
    atRiskProjects: 0,
  })

  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [deepDiveTitle, setDeepDiveTitle] = useState("");
  const [deepDiveData, setDeepDiveData] = useState([]);
  const [deepDiveType, setDeepDiveType] = useState("projects");

  const [selectedProjectForMitigation, setSelectedProjectForMitigation] = useState(null);
  const [aiMitigationModalOpen, setAiMitigationModalOpen] = useState(false);
  const [aiMitigationLoading, setAiMitigationLoading] = useState(false);
  const [aiMitigationError, setAiMitigationError] = useState(null);
  const [aiMitigationData, setAiMitigationData] = useState(null);

  const handleOpenDeepDive = (title, data, type = "projects") => {
    setDeepDiveTitle(title);
    setDeepDiveData(data);
    setDeepDiveType(type);
    setDeepDiveOpen(true);
  };

  const handleOpenProjectMitigation = async (project) => {
    try {
      setSelectedProjectForMitigation(project);
      setAiMitigationModalOpen(true);
      setAiMitigationLoading(true);
      setAiMitigationError(null);
      setAiMitigationData(null);

      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const projectId = project.id || project.project_id;

      const res = await axios.post(
        `${API_URL}/api/cpo-ppm-dashboard/project/${projectId}/ai-mitigation`,
        { customReason: project.reason || project.risk_reason || project.notes || "" },
        {
          headers: token ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (res.data?.success) {
        setAiMitigationData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to generate AI mitigation plan.");
      }
    } catch (err) {
      //console.error("AI Mitigation Error:", err);
      setAiMitigationError(err.response?.data?.message || err.message || "Failed to generate AI recommendations.");
    } finally {
      setAiMitigationLoading(false);
    }
  };

  const navigate = useNavigate()

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const getEmployeeid = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });
      if (response.data && response.data.employee_id) {
        setEmployeeId(response.data.employee_id);
        return response.data.employee_id;
      }
      return null;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      return null;
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        setError("Authentication required. Please log in.")
        setLoading(false)
        return
      }

      const payload = JSON.parse(atob(token.split(".")[1]))
      const emp_id = await getEmployeeid();

      setLoading(true)
      setError(null)

      const projectsResponse = await fetch(`${API_URL}/api/dash/dashboard?employee_id=${emp_id || ''}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      let projectsData = []
      if (projectsResponse.ok) {
        const projectsResult = await projectsResponse.json()
        if (Array.isArray(projectsResult)) {
          projectsData = projectsResult
        } else if (projectsResult && Array.isArray(projectsResult.projects)) {
          projectsData = projectsResult.projects
        } else if (projectsResult && Array.isArray(projectsResult.data)) {
          projectsData = projectsResult.data
        }
      }

      setProjects(projectsData)
      setIssues([])
      calculateDashboardStats(projectsData, [])
    } catch (err) {
      //console.error("Error fetching dashboard data:", err)
      setError(`Failed to load dashboard data. Please try again.`)
    } finally {
      setLoading(false)
    }
  }

  const calculateDashboardStats = (projectsData, issuesData) => {
    const totalProjects = projectsData.length

    const completedProjects = projectsData.filter((project) => {
      const statusStr = String(project.displayStatus || project.status || "").toLowerCase()
      const completion = project.completionPercentage || project.completion || 0
      return statusStr === "completed" || statusStr === "done" || statusStr === "closed" || statusStr === "3" || completion === 100
    }).length

    const inProgressProjects = projectsData.filter((project) => {
      const statusStr = String(project.displayStatus || project.status || "").toLowerCase()
      const completion = project.completionPercentage || project.completion || 0
      const isCompleted = statusStr === "completed" || statusStr === "done" || statusStr === "closed" || statusStr === "3" || completion === 100
      return !isCompleted
    }).length

    const atRiskProjects = projectsData.filter((project) => isProjectAtRisk(project)).length

    setDashboardStats({
      totalProjects,
      completedProjects,
      inProgressProjects,
      atRiskProjects,
    })
  }

  const getStatusName = (status) => {
    const statusStr = String(status || "").toLowerCase().trim()
    if (["completed", "done", "closed", "finished"].includes(statusStr)) return "Completed"
    if (["in progress", "in-progress", "active", "working"].includes(statusStr)) return "In Progress"
    if (["at risk", "at-risk", "blocked"].includes(statusStr)) return "At Risk"
    if (["on hold", "on-hold", "paused"].includes(statusStr)) return "On Hold"
    return "In Progress"
  }

  const getProjectStatusData = () => {
    const statusCount = {}
    projects.forEach((project) => {
      const normalizedStatus = getStatusName(project.displayStatus || project.status)
      statusCount[normalizedStatus] = (statusCount[normalizedStatus] || 0) + 1
    })

    return Object.keys(statusCount).map((status) => ({
      name: status,
      value: statusCount[status],
    }))
  }

  const handleStatusPieClick = (entry) => {
    if (!entry) return
    const statusName = entry.name || (typeof entry === "string" ? entry : (entry.payload && entry.payload.name ? entry.payload.name : ""))
    if (!statusName) return

    if (statusName === "At Risk" || statusName === "Overdue") {
      const filtered = projects.filter(isProjectAtRisk)
      handleOpenDeepDive("At-Risk & Overdue Projects Inspection", filtered, "at_risk_overdue")
    } else if (statusName === "In Progress") {
      const filtered = projects.filter((p) => {
        const norm = getStatusName(p.displayStatus || p.status)
        const isDone = (p.completionPercentage || 0) === 100 || norm === "Completed"
        return norm === "In Progress" && !isDone
      })
      handleOpenDeepDive("In Progress Projects Inspection", filtered, "projects")
    } else if (statusName === "Completed") {
      const filtered = projects.filter((p) => {
        const norm = getStatusName(p.displayStatus || p.status)
        return norm === "Completed" || (p.completionPercentage || 0) === 100
      })
      handleOpenDeepDive("Completed Projects Inspection", filtered, "projects")
    } else {
      const filtered = projects.filter((p) => getStatusName(p.displayStatus || p.status) === statusName)
      handleOpenDeepDive(`${statusName} Projects Inspection`, filtered, "projects")
    }
  }

  const getProjectScheduleData = () => {
    return activeProjects.map(project => {
      const endDate = project.endDate || project.end_date;
      const days = getDaysRemaining(endDate);
      const daysVal = days !== null ? days : 0;
      const isOverdue = daysVal < 0;

      return {
        name: project.key || (project.name ? (project.name.length > 12 ? `${project.name.substring(0, 10)}..` : project.name) : 'Project'),
        fullName: project.name,
        DaysRemaining: isOverdue ? Math.abs(daysVal) : daysVal,
        rawDays: daysVal,
        isOverdue: isOverdue,
        statusLabel: isOverdue ? `${Math.abs(daysVal)} days overdue` : daysVal <= 7 ? `${daysVal} days left (due soon)` : `${daysVal} days left`,
        fillColor: isOverdue ? '#EF4444' : daysVal <= 7 ? '#F59E0B' : '#3B82F6',
        projectId: project.id || project.project_id
      };
    });
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified"
    const options = { year: "numeric", month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString("en-US", options)
  }

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null
    const today = new Date()
    const due = new Date(endDate)
    const diffTime = due - today
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  const getStatusColor = (status) => {
    const statusStr = String(status || "").toLowerCase()
    if (["completed", "done", "closed"].includes(statusStr)) return "#10B981"
    if (["in progress", "active", "working"].includes(statusStr)) return "#3B82F6"
    if (["at risk", "blocked"].includes(statusStr)) return "#EF4444"
    if (["on hold", "paused"].includes(statusStr)) return "#F59E0B"
    return "#3B82F6"
  }

  const isProjectAtRisk = (project) => {
    const statusStr = String(project.displayStatus || project.status || "").toLowerCase()
    const completion = Number(project.completionPercentage || project.completion || 0)
    const endDate = project.endDate || project.end_date || project.dueDate

    if (statusStr === "at risk" || statusStr === "blocked" || statusStr === "overdue" || statusStr === "delayed") return true
    if (endDate) {
      const today = new Date()
      const due = new Date(endDate)
      const daysRemaining = Math.ceil((due - today) / (1000 * 60 * 60 * 24))
      if (daysRemaining < 0 && completion < 100) return true
      if (completion < 95 && daysRemaining <= 5) return true
    }
    return false
  }

  const getActiveProjects = () => {
    return projects.filter((project) => {
      const statusStr = String(project.displayStatus || project.status || "").toLowerCase().trim()
      const completion = Number(project.completionPercentage || project.completion || 0)
      const isCompleted = statusStr === "completed" || statusStr === "done" || statusStr === "closed" || statusStr === "3" || completion === 100
      return !isCompleted
    })
  }

  const CHART_COLORS = ["#3B82F6", "#10B981", "#EF4444", "#F59E0B", "#8B5CF6", "#EC4899"]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: "#F8FAFC" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} thickness={4} sx={{ color: "#3B82F6" }} />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Loading Portfolio Dashboard...
          </Typography>
        </Stack>
      </Box>
    )
  }

  const activeProjects = getActiveProjects()
  const projectStatusData = getProjectStatusData()
  const projectScheduleData = getProjectScheduleData()

  return (
    <Box sx={{ minHeight: "100vh", py: 1, }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              High-level operational overview across all company projects
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              icon={<Speed sx={{ color: "#10B981 !important", fontSize: 18 }} />}
              label={`${dashboardStats.completedProjects} of ${dashboardStats.totalProjects} Projects Delivered`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "0.813rem", height: 32, borderColor: "#CBD5E1" }}
            />
            <Tooltip title="Refresh Dashboard">
              <IconButton
                onClick={fetchDashboardData}
                size="small"
                sx={{ bgcolor: "#ffffff", border: "1px solid #CBD5E1", "&:hover": { bgcolor: "#F1F5F9" } }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} action={<Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {/* 4 PROJECT-FOCUSED KPI CARDS */}
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          {/* Card 1: Total Projects */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("All Company Projects Inspection", projects)}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  borderColor: "#3B82F6"
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Total Projects
                </Typography>
                <Avatar sx={{ bgcolor: "#EFF6FF", color: "#3B82F6", width: 40, height: 40, borderRadius: 2 }}>
                  <FolderOpen fontSize="small" />
                </Avatar>
              </Stack>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A", mb: 0.5 }}>
                {dashboardStats.totalProjects}
              </Typography>
              <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>
                ✓ {dashboardStats.completedProjects} Completed
              </Typography>
            </Card>
          </Grid>

          {/* Card 2: In Progress Projects */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("Active In-Progress Projects Inspection", activeProjects)}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  borderColor: "#3B82F6"
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  In Progress
                </Typography>
                <Avatar sx={{ bgcolor: "#F0FDF4", color: "#10B981", width: 40, height: 40, borderRadius: 2 }}>
                  <Timeline fontSize="small" />
                </Avatar>
              </Stack>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A", mb: 0.5 }}>
                {dashboardStats.inProgressProjects}
              </Typography>
              <Typography variant="caption" sx={{ color: "#3B82F6", fontWeight: 600 }}>
                ⚡ Active project delivery
              </Typography>
            </Card>
          </Grid>

          {/* Card 3: Completed Projects */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("Completed Projects Inspection", projects.filter(p => (p.completionPercentage || 0) === 100 || p.status === 'Completed'))}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  borderColor: "#10B981"
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Completed
                </Typography>
                <Avatar sx={{ bgcolor: "#ECFDF5", color: "#10B981", width: 40, height: 40, borderRadius: 2 }}>
                  <CheckCircle fontSize="small" />
                </Avatar>
              </Stack>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A", mb: 0.5 }}>
                {dashboardStats.completedProjects}
              </Typography>
              <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>
                ✓ Delivered milestones
              </Typography>
            </Card>
          </Grid>

          {/* Card 4: At Risk Projects */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("At-Risk & Overdue Projects Inspection", projects.filter(isProjectAtRisk), "at_risk_overdue")}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  borderColor: "#EF4444"
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  At Risk / Overdue
                </Typography>
                <Avatar sx={{ bgcolor: "#FEF2F2", color: "#EF4444", width: 40, height: 40, borderRadius: 2 }}>
                  <Warning fontSize="small" />
                </Avatar>
              </Stack>
              <Typography variant="h5" fontWeight={700} sx={{ color: dashboardStats.atRiskProjects > 0 ? "#EF4444" : "#0F172A", mb: 0.5 }}>
                {dashboardStats.atRiskProjects}
              </Typography>
              <Typography variant="caption" sx={{ color: dashboardStats.atRiskProjects > 0 ? "#EF4444" : "#10B981", fontWeight: 600 }}>
                {dashboardStats.atRiskProjects > 0 ? "🚨 Requires review" : "✓ On schedule"}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* DUAL CHARTS SECTION */}
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          {/* Chart 1: Project Delivery Timeline & Schedule Velocity */}
          <Grid item xs={12} lg={7}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                p: 2.5,
                height: "100%"
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0F172A" }}>
                Project Delivery Timeline & Schedule Velocity
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={2} display="block">
                Days remaining until target delivery date (Overdue in red, Due soon in amber)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {projectScheduleData.length > 0 ? (
                <Box sx={{ height: 260, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={projectScheduleData}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      style={{ cursor: "pointer" }}
                      onClick={(state) => {
                        if (state && state.activePayload && state.activePayload.length > 0) {
                          const clickedData = state.activePayload[0].payload;
                          if (clickedData.projectId) {
                            navigate(`/project/${clickedData.projectId}/db`);
                          }
                        }
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
                      <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} domain={[0, 'auto']} label={{ value: 'Days', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748B' } }} />
                      <RechartsTooltip formatter={(value, name, item) => [item.payload.isOverdue ? `${value} Days Overdue (Past Deadline)` : `${value} Days Remaining`, item.payload.fullName || 'Project']} />
                      <Bar dataKey="DaysRemaining" radius={[4, 4, 0, 0]} barSize={28}>
                        {projectScheduleData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fillColor} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body2" color="text.secondary">No project schedule metrics available</Typography>
                </Box>
              )}
            </Card>
          </Grid>

          {/* Chart 2: Portfolio Health Donut Chart */}
          <Grid item xs={12} lg={5}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                p: 2.5,
                height: "100%"
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0F172A" }}>
                Portfolio Health & Status Breakdown
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={2} display="block">
                Distribution of projects across delivery stages
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {projectStatusData.length > 0 ? (
                <Box sx={{ height: 260, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={projectStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={4}
                        dataKey="value"
                        style={{ cursor: "pointer" }}
                        onClick={(entry) => handleStatusPieClick(entry)}
                      >
                        {projectStatusData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={CHART_COLORS[index % CHART_COLORS.length]} 
                            style={{ cursor: "pointer" }}
                            onClick={() => handleStatusPieClick(entry)}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend 
                        verticalAlign="bottom" 
                        height={32} 
                        wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }} 
                        onClick={(entry) => handleStatusPieClick(entry)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body2" color="text.secondary">No status metrics available</Typography>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* FULL WIDTH ACTIVE PROJECT EXECUTION TABLE */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#ffffff",
            border: "1px solid #E2E8F0",
            p: 2.5,
            mb: 3
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0F172A" }}>
                Active Project & Delivery Overview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Live project tracking, assigned leadership, and completion progress
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowForward fontSize="small" />}
              onClick={() => navigate('/pro')}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.813rem" }}
            >
              View All Projects
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: "1px solid #F1F5F9" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell><strong>PROJECT & KEY</strong></TableCell>
                  <TableCell><strong>LEADERSHIP</strong></TableCell>
                  <TableCell><strong>COMPLETION PROGRESS</strong></TableCell>
                  <TableCell><strong>TARGET END DATE</strong></TableCell>
                  <TableCell align="right"><strong>ACTION</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeProjects.length > 0 ? (
                  activeProjects.map((project) => {
                    const completion = project.completionPercentage || 0
                    const endDate = project.endDate || project.end_date
                    const daysRemaining = getDaysRemaining(endDate)
                    const atRisk = isProjectAtRisk(project)

                    return (
                      <TableRow key={project.id || project.project_id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ bgcolor: "#3B82F6", width: 34, height: 34, fontWeight: 700, fontSize: "0.85rem" }}>
                              {(project.name || "P").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#0F172A" }}>
                                {project.name}
                              </Typography>
                              <Chip label={project.key || project.projectKey || "PROJ"} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            PO: {project.product_owner_name || project.lead?.name || 'Not assigned'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            PM: {project.product_manager_name || 'Not assigned'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ minWidth: 180 }}>
                          <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" color="text.secondary">Progress</Typography>
                            <Typography variant="caption" fontWeight={700}>{completion}%</Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={completion}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "#E2E8F0",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: completion >= 80 ? "#10B981" : completion >= 40 ? "#3B82F6" : "#F59E0B",
                                borderRadius: 3
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">{formatDate(endDate)}</Typography>
                          {atRisk ? (
                            <Chip label="AT RISK" size="small" color="error" sx={{ fontWeight: 700, height: 20, fontSize: "0.65rem" }} />
                          ) : (
                            <Chip
                              label={daysRemaining !== null ? (daysRemaining > 0 ? `${daysRemaining}d left` : "Overdue") : "Active"}
                              size="small"
                              color={daysRemaining !== null && daysRemaining <= 7 ? "warning" : "success"}
                              variant="outlined"
                              sx={{ fontWeight: 600, height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/project/${project.id || project.project_id}/db`)}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No active projects found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Interactive Deep-Dive Inspection Modal */}
        <Dialog
          open={deepDiveOpen}
          onClose={() => setDeepDiveOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F8FAFC', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {deepDiveTitle}
              </Typography>
            </Box>
            <IconButton onClick={() => setDeepDiveOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {deepDiveType === "at_risk_overdue" ? (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b", py: 1.5 }}>Project & Key</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Leadership</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Target End Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Progress</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Overdue / Schedule</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deepDiveData.length > 0 ? deepDiveData.map((proj) => {
                      const daysRemaining = getDaysRemaining(proj.endDate || proj.end_date);
                      const isOverdue = daysRemaining !== null && daysRemaining < 0;
                      return (
                        <TableRow 
                          key={proj.id || proj.project_id} 
                          hover
                          onClick={() => handleOpenProjectMitigation(proj)}
                          sx={{ 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f8fafc" },
                            "&:last-child td, &:last-child th": { border: 0 }
                          }}
                        >
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#0F172A" }}>
                              {proj.name}
                            </Typography>
                            <Chip label={proj.key || proj.projectKey || 'PROJ'} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>
                              PO: {proj.product_owner_name || proj.lead?.name || 'Not assigned'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              PM: {proj.product_manager_name || 'Not assigned'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                            {formatDate(proj.endDate || proj.end_date)}
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            <LinearProgress variant="determinate" value={proj.completionPercentage || 0} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                            <Typography variant="caption" fontWeight={700}>{proj.completionPercentage || 0}% Completed</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={isOverdue ? "Overdue" : (proj.status || "At Risk")} 
                              size="small" 
                              sx={{ 
                                fontWeight: 700, 
                                fontSize: "0.75rem",
                                bgcolor: isOverdue ? "#fee2e2" : "#fef3c7",
                                color: isOverdue ? "#dc2626" : "#d97706"
                              }} 
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: isOverdue ? "#dc2626" : "#d97706" }}>
                              {isOverdue ? `${Math.abs(daysRemaining)} days overdue` : (daysRemaining !== null ? `${daysRemaining}d left` : "At Risk")}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#64748b" }}>No overdue or at-risk projects found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                    <TableRow>
                      <TableCell><strong>LEADERSHIP</strong></TableCell>
                      <TableCell><strong>PROJECT & KEY</strong></TableCell>
                      <TableCell><strong>SCHEDULE & STATUS</strong></TableCell>
                      <TableCell><strong>PROGRESS (%)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deepDiveData.length > 0 ? deepDiveData.map((proj) => (
                      <TableRow 
                        key={proj.id || proj.project_id} 
                        hover
                        onClick={() => {
                          setDeepDiveOpen(false);
                          navigate(`/project/${proj.id || proj.project_id}/db`);
                        }}
                        sx={{ 
                          cursor: "pointer", 
                          "&:hover": { bgcolor: "#f8fafc" },
                          "&:last-child td, &:last-child th": { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>
                            PO: {proj.product_owner_name || proj.lead?.name || 'Not assigned'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PM: {proj.product_manager_name || 'Not assigned'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>{proj.name}</Typography>
                          <Chip label={proj.key || proj.projectKey || 'PROJ'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(proj.startDate || proj.start_date)} - {formatDate(proj.endDate || proj.end_date)}</Typography>
                          <Chip label={proj.status || 'Active'} size="small" color={getStatusColor(proj.status) === '#10B981' ? 'success' : 'primary'} />
                        </TableCell>
                        <TableCell>
                          <LinearProgress variant="determinate" value={proj.completionPercentage || 0} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                          <Typography variant="caption" fontWeight={600}>{proj.completionPercentage || 0}% Completed</Typography>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No matching records found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </Dialog>

        {/* Project AI Mitigation Plan Modal Dialog */}
        <Dialog
          open={aiMitigationModalOpen}
          onClose={() => setAiMitigationModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "14px", overflow: "hidden" }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#FEF2F2', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning sx={{ color: "#EF4444" }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: "#991B1B", fontSize: "1.1rem" }}>
                AI Risk & Mitigation Profile: {selectedProjectForMitigation?.name || selectedProjectForMitigation?.key || "Project"}
              </Typography>
            </Box>
            <IconButton onClick={() => setAiMitigationModalOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedProjectForMitigation && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Project Deliverable Summary
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.3, color: "#0F172A" }}>
                      {selectedProjectForMitigation.name} ({selectedProjectForMitigation.key || selectedProjectForMitigation.projectKey || "PROJ"})
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Chip 
                      label={`Progress: ${selectedProjectForMitigation.completionPercentage || 0}%`} 
                      size="small" 
                      color={(selectedProjectForMitigation.completionPercentage || 0) >= 80 ? "success" : "primary"}
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px" }} 
                    />
                    <Chip 
                      label={`End Date: ${formatDate(selectedProjectForMitigation.endDate || selectedProjectForMitigation.end_date)}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px" }} 
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* 3-Column Layout: Reason | Mitigation Plan | Action Items */}
                <Grid container spacing={2.5}>
                  {/* Column 1: Reason / Root Cause */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#fff7ed", p: 2, borderRadius: "10px", borderLeft: "4px solid #f97316", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <Warning sx={{ fontSize: 16, color: "#ea580c" }} />
                        <Typography variant="caption" sx={{ color: "#9a3412", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Reason / Root Cause
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#334155", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                        {selectedProjectForMitigation.reason || selectedProjectForMitigation.risk_reason || selectedProjectForMitigation.notes || "Scope expansion, sprint backlog spillover, and critical dependency bottlenecks impacting delivery."}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Column 2: Mitigation Plan */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#f0fdf4", p: 2, borderRadius: "10px", borderLeft: "4px solid #16a34a", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <CheckCircle sx={{ fontSize: 16, color: "#16a34a" }} />
                        <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Mitigation Plan
                        </Typography>
                      </Box>
                      {aiMitigationLoading ? (
                        <Box display="flex" alignItems="center" gap={1.2} py={2}>
                          <CircularProgress size={18} sx={{ color: "#16a34a" }} />
                          <Typography variant="body2" sx={{ color: "#15803d", fontStyle: "italic", fontSize: "0.82rem" }}>
                            Generating mitigation steps...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#166534", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                          {aiMitigationData?.mitigation || (aiMitigationData?.mitigationPlan && (Array.isArray(aiMitigationData.mitigationPlan) ? aiMitigationData.mitigationPlan.join("\n") : aiMitigationData.mitigationPlan)) || "Re-baseline remaining milestones, de-scope secondary backlog items, and allocate specialized buffer capacity."}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Column 3: Action Items */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#eff6ff", p: 2, borderRadius: "10px", borderLeft: "4px solid #2563eb", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <FolderOpen sx={{ fontSize: 16, color: "#2563eb" }} />
                        <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Action Item & Owner
                        </Typography>
                      </Box>
                      {aiMitigationLoading ? (
                        <Box display="flex" alignItems="center" gap={1.2} py={2}>
                          <CircularProgress size={18} sx={{ color: "#2563eb" }} />
                          <Typography variant="body2" sx={{ color: "#1e40af", fontStyle: "italic", fontSize: "0.82rem" }}>
                            Generating action items & owner...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#1e3a8a", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                          {aiMitigationData?.action || (aiMitigationData?.actionItems && (Array.isArray(aiMitigationData.actionItems) ? aiMitigationData.actionItems.map(a => typeof a === 'object' ? `${a.action || a.item} (Owner: ${a.owner || 'Lead'})` : a).join("\n") : aiMitigationData.actionItems)) || `Conduct emergency sprint review with ${selectedProjectForMitigation.product_owner_name || selectedProjectForMitigation.lead?.name || 'Project Manager'} and execute dependency resolution plan.`}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  )
}

export default Dashboard